import { defineStore } from 'pinia';
import { reactive } from 'vue';
import useAppSettingsStore from '@/store/modules/appSettings.js';
import useAuthStore from '@/store/modules/auth.js';
import useLocaleStore from '@/store/modules/locale.js';
import { useLocalStorageHook } from '@/hooks/usePersistentStorage.js';
import { assignState } from '@/store/index.js';
import {
  apiRequestPin,
  apiRequestPinInApp,
  apiVerifyPin,
  apiVerifyPinInApp,
  apiGetStatusForBrainiac,
} from '@/services/general.js';
import { apiGetUserStatus, apiUpdateAuthUserLanguage, apiHEUserRegister } from '@/services/user.js';
import { registerMsisdnStorageKey } from '@/constants/persistentStorage.js';

const getUrlParamsDefaultState = () => ({
  operator: '',
  correlationId: '',
  clickId: '',
  userMsisdn: '',
  msisdn: '',
  refUrl: '',
  campaignId: '',
  publisherId: '',
  heHash: '',
  heMsisdn: '',
  errorMessage: '',
  errorMsisdn: '',
});

const useRegisterStore = defineStore('register', () => {
  const appSettingsStore = useAppSettingsStore();
  const authStore = useAuthStore();
  const localeStore = useLocaleStore();

  const [registrationMsisdn, setRegistrationMsisdn] = useLocalStorageHook(registerMsisdnStorageKey, '');

  const urlParams = reactive(getUrlParamsDefaultState());

  const setUrlParams = (data) => {
    const { cId, clickid, ...rest } = data;

    const transformedProps = {};

    if (cId) {
      transformedProps.correlationId = cId;
    }

    if (clickid) {
      transformedProps.clickId = clickid;
    }

    assignState(urlParams, { ...rest, ...transformedProps });
  };

  const dispatchHEUserRegister = async ({ msisdn }) => {
    try {
      const queryParams = {
        msisdn,
        hash: encodeURIComponent(urlParams.heHash),
        lang: localeStore.currentLanguage,
        cid: urlParams.correlationId,
      };

      const { parsedData } = await apiHEUserRegister({ queryParams });

      authStore.setJwtToken(parsedData.user_auth_token);

      await authStore.dispatchRetrieveAuthUser();

      if (authStore.user.language !== localeStore.currentLanguage) {
        await apiUpdateAuthUserLanguage({ language: localeStore.currentLanguage });
      }

      await Promise.allSettled(authStore.getPostLoginRequestList().map(action => action()));
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const dispatchRequestPin = async ({ msisdn, operator }) => {
    try {
      const queryParams = {
        msisdn,
        lang: localeStore.currentLanguage,
      };

      if (operator) {
        queryParams.operator = operator;
      }

      const { parsedData } = await apiRequestPin({ queryParams });

      if (operator) {
        await appSettingsStore.dispatchRetrieveMultiOperatorProperties({ operator });
      }

      setRegistrationMsisdn(parsedData);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const dispatchRequestPinInApp = async ({ msisdn, operator }) => {
    try {
      const queryParams = {
        msisdn,
        lang: localeStore.currentLanguage,
      };

      if (operator) {
        queryParams.operator = operator;
      }

      await apiRequestPinInApp({ queryParams });

      setRegistrationMsisdn(msisdn);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const dispatchVerifyPin = async ({ msisdn, pin, rememberMe }) => {
    try {
      const verifyPinQueryParams = {
        msisdn,
        pin,
        rememberMe,
        clickId: urlParams.clickId,
        refUrl: urlParams.refUrl,
        campaignId: urlParams.campaignId,
        publisherId: urlParams.campaignId,
      };

      await apiVerifyPin({ queryParams: verifyPinQueryParams });
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const dispatchVerifyPinWebapp = async ({ msisdn, pin }) => {
    try {
      const verifyPinQueryParams = {
        msisdn,
        pin,
        clickid: urlParams.clickId,
        refUrl: urlParams.refUrl,
        campaignId: urlParams.campaignId,
        publisherId: urlParams.publisherId,
      };

      await apiVerifyPin({ queryParams: verifyPinQueryParams });
    } catch (error) {
      return Promise.reject(error);
    }
  };

  // The backend retries the status check against Core internally, so this single
  // call may take up to a minute before it resolves or rejects.
  const dispatchWaitForSubscriptionStatus = async ({ msisdn }) => {
    try {
      const queryParams = {
        msisdn,
        lang: localeStore.currentLanguage,
        clickid: urlParams.clickId,
        refUrl: urlParams.refUrl,
        campaignId: urlParams.campaignId,
        publisherId: urlParams.publisherId,
      };

      const { parsedData: userStatusResponseParsedData } = await apiGetStatusForBrainiac({ queryParams });

      authStore.setJwtToken(userStatusResponseParsedData.user_auth_token);

      await authStore.dispatchRetrieveAuthUser();

      if (authStore.user.language !== localeStore.currentLanguage) {
        await apiUpdateAuthUserLanguage({ language: localeStore.currentLanguage });
      }

      await Promise.allSettled(authStore.getPostLoginRequestList().map(action => action()));

      return {
        userStatusResponseParsedData,
      };
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const dispatchVerifyPinInApp = async ({ msisdn, pin }) => {
    try {
      const verifyPinQueryParams = {
        msisdn,
        pin,
        lang: localeStore.currentLanguage,
        clickId: urlParams.clickId,
        refUrl: urlParams.refUrl,
        campaignId: urlParams.campaignId,
        publisherId: urlParams.campaignId,
        webflow: true,
      };

      await apiVerifyPinInApp({ queryParams: verifyPinQueryParams });

      const getUserStatusQueryParams = {
        msisdn,
        pin,
        lang: localeStore.currentLanguage,
      };

      const { parsedData: userStatusResponseParsedData } = await apiGetUserStatus({ queryParams: getUserStatusQueryParams });

      authStore.setJwtToken(userStatusResponseParsedData.user_auth_token);

      await authStore.dispatchRetrieveAuthUser();

      if (authStore.user.language !== localeStore.currentLanguage) {
        await apiUpdateAuthUserLanguage({ language: localeStore.currentLanguage });
      }

      await Promise.allSettled(authStore.getPostLoginRequestList().map(action => action()));

      return {
        userStatusResponseParsedData,
      };
    } catch (error) {
      return Promise.reject(error);
    }
  };

  return {
    registrationMsisdn,
    urlParams,
    setRegistrationMsisdn,
    setUrlParams,
    dispatchHEUserRegister,
    dispatchRequestPin,
    dispatchRequestPinInApp,
    dispatchVerifyPin,
    dispatchVerifyPinWebapp,
    dispatchVerifyPinInApp,
    dispatchWaitForSubscriptionStatus,
  };
});

export default useRegisterStore;
